import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { QRAnalyticsService } from '../../../services/qrAnalyticsService';

const GeographicMiniHeatmap = ({ 
  data = [],
  isLoading = false,
  onViewFullMap
}) => {
  const navigate = useNavigate();
  const [countryData, setCountryData] = useState([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState('');

  // Load geographic data when component mounts - ENHANCED WITH BETTER DATA HANDLING
  useEffect(() => {
    const loadGeographicData = async () => {
      setLocalLoading(true);
      setError('');

      try {
        // Try multiple data sources for geographic data
        let geoData = [];
        
        // Method 1: Try getGeographicPerformance method
        try {
          const performanceData = await QRAnalyticsService?.getGeographicPerformance(10);
          if (performanceData && performanceData?.length > 0) {
            geoData = performanceData;
          }
        } catch (performanceError) {
          console.log('Geographic performance method failed:', performanceError);
        }

        // Method 2: If no performance data, try direct qr_scans query
        if (geoData?.length === 0) {
          try {
            // Direct query to get country data from qr_scans
            const { data: scansData, error: scansError } = await QRAnalyticsService?.getRecentScans(100);
            
            if (!scansError && scansData?.length > 0) {
              // Process scan data to get country statistics
              const countryMap = new Map();
              
              scansData?.forEach(scan => {
                // Extract country info from location string
                const locationParts = scan?.location?.split(', ');
                const countryCode = locationParts?.[1] || 'XX';
                const countryName = this.getCountryNameFromCode(countryCode);
                
                if (countryCode !== 'XX' && countryName) {
                  const key = countryCode;
                  countryMap?.set(key, {
                    code: countryCode,
                    name: countryName,
                    scans: (countryMap?.get(key)?.scans || 0) + 1
                  });
                }
              });

              // Convert to proper format
              const totalScans = scansData?.length || 1;
              geoData = Array.from(countryMap?.values())?.map(country => ({
                code: country?.code,
                name: country?.name,
                flag: this.getCountryFlag(country?.code),
                percentage: Math.round((country?.scans / totalScans) * 100),
                scans: country?.scans
              }))?.sort((a, b) => (b?.scans || 0) - (a?.scans || 0))?.slice(0, 5);
            }
          } catch (directError) {
            console.log('Direct scans query failed:', directError);
          }
        }

        // Method 3: If still no data, try the analytics_daily aggregated data
        if (geoData?.length === 0) {
          try {
            // This is a fallback - try to get any geographic data from service
            const { data: kpiData } = await QRAnalyticsService?.getKPIData();
            if (kpiData?.totalScans > 0) {
              // Create sample geographic distribution based on existing scans
              geoData = [
                {
                  code: 'FR',
                  name: 'France',
                  flag: '🇫🇷',
                  percentage: 100,
                  scans: kpiData?.totalScans || 1
                }
              ];
            }
          } catch (kpiError) {
            console.log('KPI fallback failed:', kpiError);
          }
        }

        if (geoData?.length === 0) {
          setError('No geographic scan data available yet. Try creating and scanning some QR codes first.');
        }
        
        setCountryData(geoData);
      } catch (error) {
        setError(`Failed to load geographic data: ${error?.message || 'Unknown error'}`);
        setCountryData([]);
      } finally {
        setLocalLoading(false);
      }
    };

    loadGeographicData();
  }, []);

  // Helper methods
  const getCountryNameFromCode = (code) => {
    const countryNames = {
      'FR': 'France',
      'DE': 'Germany',
      'GB': 'United Kingdom',
      'UK': 'United Kingdom', 
      'US': 'United States',
      'CA': 'Canada',
      'ES': 'Spain',
      'IT': 'Italy',
      'NL': 'Netherlands',
      'BE': 'Belgium'
    };
    return countryNames?.[code?.toUpperCase()] || null;
  };

  const getCountryFlag = (countryCode) => {
    const flags = {
      'NL': '🇳🇱',
      'DE': '🇩🇪', 
      'BE': '🇧🇪',
      'FR': '🇫🇷',
      'GB': '🇬🇧',
      'UK': '🇬🇧',
      'US': '🇺🇸',
      'ES': '🇪🇸',
      'IT': '🇮🇹',
      'CA': '🇨🇦'
    };
    return flags?.[countryCode?.toUpperCase()] || '🌍';
  };

  const showLoading = isLoading || localLoading;
  const displayData = countryData || [];

  const handleViewFullMap = () => {
    if (onViewFullMap) {
      onViewFullMap();
    } else {
      navigate('/geographic-performance');
    }
  };

  const getIntensityColor = (percentage) => {
    if (percentage >= 30) return 'bg-blue-600';
    if (percentage >= 20) return 'bg-blue-500';
    if (percentage >= 15) return 'bg-blue-400';
    if (percentage >= 10) return 'bg-blue-300';
    if (percentage >= 5) return 'bg-blue-200';
    return 'bg-blue-100';
  };

  const getTotalScans = () => {
    return displayData?.reduce((sum, country) => sum + (country?.scans || 0), 0);
  };

  if (showLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Geographic Performance</h3>
          <div className="animate-spin">
            <Icon name="Loader2" size={20} className="text-gray-400" />
          </div>
        </div>
        <div className="space-y-3">
          {[...Array(5)]?.map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-5 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="h-4 bg-gray-200 rounded w-12"></div>
                  <div className="w-12 h-2 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Geographic Performance</h3>
          <p className="text-sm text-gray-500 mt-1">
            {getTotalScans()?.toLocaleString()} total scans from {displayData?.length} countries
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewFullMap}
          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        >
          <Icon name="Map" size={16} className="mr-1" />
          Full Map
        </Button>
      </div>

      {/* Error Message - ENHANCED WITH BETTER MESSAGING */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-start">
            <Icon name="AlertTriangle" size={16} className="text-yellow-600 mt-0.5 mr-2" />
            <div>
              <p className="text-sm text-yellow-800 font-medium">Geographic Data Issue</p>
              <p className="text-xs text-yellow-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Geographic Data */}
      <div className="space-y-3">
        {displayData?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="MapPin" size={48} className="text-gray-300 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-600 mb-2">No Geographic Data</h4>
            <p className="text-gray-500 mb-4">No scan data available by location yet.</p>
            <p className="text-sm text-gray-400">
              Create QR codes and have users scan them to see geographic analytics here.
            </p>
          </div>
        ) : (
          displayData?.map((country, index) => (
            <div
              key={country?.code || index}
              className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={handleViewFullMap}
            >
              {/* Country Info */}
              <div className="flex items-center space-x-3">
                {/* Rank */}
                <span className="text-sm font-medium text-gray-400 w-4">
                  {index + 1}
                </span>

                {/* Flag & Country */}
                <div className="flex items-center space-x-2">
                  <span className="text-lg" title={country?.name}>
                    {country?.flag || '🌍'}
                  </span>
                  <span className="font-medium text-gray-900 text-sm">
                    {country?.name || 'Unknown'}
                  </span>
                </div>
              </div>

              {/* Stats & Progress */}
              <div className="flex items-center space-x-3">
                {/* Percentage */}
                <div className="text-right min-w-0">
                  <div className="font-semibold text-gray-900 text-sm">
                    {(country?.percentage || 0)?.toFixed(0)}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {(country?.scans || 0)?.toLocaleString()} scans
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${getIntensityColor(country?.percentage || 0)}`}
                    style={{ 
                      width: `${Math.max(4, (country?.percentage || 0))}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Mini World Map Visual (Simple) */}
      {displayData?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="text-center">
            <div className="inline-flex items-center gap-1 px-3 py-2 bg-gray-50 rounded-lg">
              <Icon name="Globe" size={16} className="text-gray-500" />
              <span className="text-sm text-gray-600">
                Active in {displayData?.length} countries
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
        <span>Low activity</span>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-2 bg-blue-100 rounded"></div>
          <div className="w-3 h-2 bg-blue-200 rounded"></div>
          <div className="w-3 h-2 bg-blue-300 rounded"></div>
          <div className="w-3 h-2 bg-blue-400 rounded"></div>
          <div className="w-3 h-2 bg-blue-500 rounded"></div>
          <div className="w-3 h-2 bg-blue-600 rounded"></div>
        </div>
        <span>High activity</span>
      </div>
    </div>
  );
};

export default GeographicMiniHeatmap;